/**
 * MED-1290 — Dev-only harness for the matched-groups import preview messaging.
 *
 * Renders the REAL UploadMatchedGroupsModal with groupsApi.previewImport swapped
 * for a stub, so the step-2 preview can be inspected in each of the three states
 * the fix cares about without auth, a cohort, or a backend:
 *
 *   1. Unreadable only  — no group could be read; zero groups, Continue blocked.
 *   2. Mixed            — some groups read, some rows not; Continue allowed.
 *   3. Clean            — every value readable; no diagnostic card at all (AC9).
 *
 * The stub returns the payload the server now produces for each case (proven
 * separately by tests/services/GroupImportService.groupColumn.test.ts) — this
 * page verifies the RENDERING of that payload, not the parse.
 *
 * No auth, no DB. Compiled out of production builds. Visit
 * /dev/group-import?s=unreadable, ?s=mixed, or ?s=clean.
 *
 * The scenario comes from the query string rather than an on-page toggle: the
 * modal closes on outside click and resets its upload state, so a button sitting
 * beside it would clear the file you had just dropped.
 *
 * Verify: upload any CSV with headers, confirm Email + Group are mapped, click
 * Continue, and read the preview.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Code, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { UploadMatchedGroupsModal } from '../cohorts/components/UploadMatchedGroupsModal';
import {
  groupsApi,
  type GroupImportPreviewResponse,
} from '../../services/api/groups';

type Scenario = 'unreadable' | 'mixed' | 'clean';

const participant = (email: string, groupNumber: number | null) => ({
  email,
  firstName: email.split('@')[0],
  lastName: 'Example',
  role: 'participant',
  groupNumber,
  matched: true,
  alreadyAssigned: false,
});

const PAYLOADS: Record<Scenario, GroupImportPreviewResponse> = {
  // The reported failure: an entire column of "GSALS-n" style values.
  unreadable: {
    groups: [],
    unassigned: [
      participant('ada@example.com', null),
      participant('grace@example.com', null),
      participant('alan@example.com', null),
      participant('edsger@example.com', null),
      participant('barbara@example.com', null),
      participant('donald@example.com', null),
    ],
    summary: {
      totalGroups: 0,
      totalMatched: 6,
      totalUnmatched: 0,
      totalAlreadyAssigned: 0,
    },
    groupColumnInfo: {
      unreadableValues: [
        'GSALS-1',
        'GSALS-2',
        'GSALS-3',
        '1-Alpha',
        '2b',
        'TBD',
        'N/A',
      ],
      unreadableCount: 6,
      emptyCount: 0,
    },
  },
  // Partial: two real groups plus a few rows that couldn't be placed.
  mixed: {
    groups: [
      {
        groupNumber: 1,
        name: 'Group 1',
        participants: [
          participant('ada@example.com', 1),
          participant('grace@example.com', 1),
        ],
      },
      {
        groupNumber: 2,
        name: 'Group 2',
        participants: [participant('alan@example.com', 2)],
      },
    ],
    unassigned: [
      participant('edsger@example.com', null),
      participant('barbara@example.com', null),
    ],
    summary: {
      totalGroups: 2,
      totalMatched: 5,
      totalUnmatched: 0,
      totalAlreadyAssigned: 0,
    },
    groupColumnInfo: {
      unreadableValues: ['1-Alpha'],
      unreadableCount: 1,
      emptyCount: 1,
    },
  },
  // Control: nothing wrong, so no card should appear.
  clean: {
    groups: [
      {
        groupNumber: 1,
        name: 'Group 1',
        participants: [
          participant('ada@example.com', 1),
          participant('grace@example.com', 1),
        ],
      },
      {
        groupNumber: 2,
        name: 'Group 2',
        participants: [
          participant('alan@example.com', 2),
          participant('edsger@example.com', 2),
        ],
      },
    ],
    unassigned: [],
    summary: {
      totalGroups: 2,
      totalMatched: 4,
      totalUnmatched: 0,
      totalAlreadyAssigned: 0,
    },
  },
};

export function GroupImportPreviewDevPage() {
  const [searchParams] = useSearchParams();
  const param = searchParams.get('s');
  const scenario: Scenario =
    param === 'mixed' || param === 'clean' ? param : 'unreadable';
  const [opened, setOpened] = useState(true);

  // Swap the API singleton's preview call for the selected fixture, restoring it
  // on unmount so nothing leaks into another dev route.
  useEffect(() => {
    const original = groupsApi.previewImport;
    groupsApi.previewImport = async () => PAYLOADS[scenario];
    return () => {
      groupsApi.previewImport = original;
    };
  }, [scenario]);

  return (
    <Stack p="xl" gap="md">
      <Title order={3}>MED-1290 — group import preview messaging</Title>
      <Text size="sm" c="dimmed">
        Upload any CSV with a header row, confirm Email and Group are mapped,
        then click Continue. Each scenario is its own URL, since dismissing the
        modal resets the upload.
      </Text>

      <Paper withBorder p="md">
        <Group>
          {(['unreadable', 'mixed', 'clean'] as Scenario[]).map(s => (
            <Button
              key={s}
              component={Link}
              to={`/dev/group-import?s=${s}`}
              reloadDocument
              variant={scenario === s ? 'filled' : 'default'}
            >
              {s}
            </Button>
          ))}
          {!opened && (
            <Button variant="light" onClick={() => setOpened(true)}>
              Reopen modal
            </Button>
          )}
        </Group>
        <Text size="xs" c="dimmed" mt="sm">
          Active payload:{' '}
          <Code>
            {JSON.stringify(PAYLOADS[scenario].groupColumnInfo ?? null)}
          </Code>
        </Text>
      </Paper>

      <UploadMatchedGroupsModal
        opened={opened}
        onClose={() => setOpened(false)}
        cohortId="dev-cohort-1290"
        onImportComplete={() => {}}
        hasSessionDates
        hasTimeSlots
      />
    </Stack>
  );
}
